/**
 * Transactions.js — Refactored (2,474 → ~540 lines)
 * All dialog components extracted to /components/transactions/
 * This file: state, data fetching, transaction handlers, layout.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { StepGuide } from '../components/onboarding';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { TooltipProvider } from '../components/ui/tooltip';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Search, AlertTriangle, FileText, FileSpreadsheet,
  FolderUp, TrendingUp, FileOutput, CheckSquare, Square,
} from 'lucide-react';

import {
  SummaryCards, BulkActionsBar, TransactionRow, IrsLineDialog,
  DraftsReviewDialog, ImportDocsDialog, PnlImportDialog, CsvImportDialog,
} from '../components/transactions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Category lists ─────────────────────────────────────────────────────────────
const BASE_CATEGORIES = [
  { id: 'advertising', name: 'Advertising' },
  { id: 'car_and_truck', name: 'Car & Truck' },
  { id: 'commissions', name: 'Commissions & Fees' },
  { id: 'contract_labor', name: 'Contract Labor' },
  { id: 'depreciation', name: 'Depreciation' },
  { id: 'employee_benefits', name: 'Employee Benefits' },
  { id: 'insurance', name: 'Insurance' },
  { id: 'interest_mortgage', name: 'Interest - Mortgage' },
  { id: 'interest_other', name: 'Interest - Other' },
  { id: 'legal_professional', name: 'Legal & Professional' },
  { id: 'office_expense', name: 'Office Expense' },
  { id: 'pension', name: 'Pension Plans' },
  { id: 'rent_vehicles', name: 'Rent - Vehicles' },
  { id: 'rent_property', name: 'Rent - Property' },
  { id: 'repairs', name: 'Repairs & Maintenance' },
  { id: 'supplies', name: 'Supplies' },
  { id: 'taxes_licenses', name: 'Taxes & Licenses' },
  { id: 'travel', name: 'Travel' },
  { id: 'meals', name: 'Meals (50%)' },
  { id: 'utilities', name: 'Utilities' },
  { id: 'wages', name: 'Wages' },
  { id: 'home_office', name: 'Home Office' },
  { id: 'health_insurance', name: 'Health Insurance' },
  { id: 'other_expense', name: 'Other Expense' },
];

const INCOME_CATEGORIES = [
  { id: 'consulting', name: 'Consulting' },
  { id: 'freelance', name: 'Freelance' },
  { id: 'services', name: 'Services' },
  { id: 'sales', name: 'Sales' },
  { id: 'rental', name: 'Rental Income' },
  { id: 'interest_income', name: 'Interest Income' },
  { id: 'dividends', name: 'Dividends' },
  { id: 'other_income', name: 'Other Income' },
];

const NEEDS_REVIEW = new Set(['other_expense', 'other', '', null, undefined]);

const EMPTY_FORM = { type: 'expense', date: new Date().toISOString().split('T')[0], description: '', amount: '', category: 'other_expense', is_deductible: true, deductible_percentage: 100, status: 'draft' };

export default function Transactions() {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();

  // ── Core data ──────────────────────────────────────────────────────────────
  const [transactions, setTransactions]     = useState([]);
  const [loading, setLoading]               = useState(true);
  const [selectedYear, setSelectedYear]     = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [filterStatus,  setFilterStatus]  = useState('all');
  const [filterType,    setFilterType]    = useState('all');
  const [filterReview,  setFilterReview]  = useState(false);
  const [searchTerm,    setSearchTerm]    = useState('');

  // ── Selection + bulk actions ───────────────────────────────────────────────
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [bulkDeleteArmed,       setBulkDeleteArmed]      = useState(false);
  const [autoCategorizing,      setAutoCategorizing]     = useState(false);

  // ── Add-transaction form ───────────────────────────────────────────────────
  const [dialogOpen,      setDialogOpen]      = useState(false);
  const [formData,        setFormData]        = useState(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // ── Dialog open states ─────────────────────────────────────────────────────
  const [importDocsOpen,  setImportDocsOpen]  = useState(false);
  const [csvDialogOpen,   setCsvDialogOpen]   = useState(false);
  const [pnlDialogOpen,   setPnlDialogOpen]   = useState(false);
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [parseDialogOpen, setParseDialogOpen] = useState(false);
  const [irsDialogOpen,   setIrsDialogOpen]   = useState(false);
  const [selectedIrsLine, setSelectedIrsLine] = useState(null);

  // ── AI parse ───────────────────────────────────────────────────────────────
  const [parseContent, setParseContent] = useState('');
  const [parsing,      setParsing]      = useState(false);

  // ── Summary totals from backend (Commandment II: no client-side reduce) ──────
  const [transactionSummary, setTransactionSummary] = useState({ total_income: 0, total_expenses: 0 });

  const filteredTransactions = transactions.filter(t => {
    if (!t.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterType   !== 'all' && t.type   !== filterType)   return false;
    if (filterReview) {
      if (t.type === 'income') return false;
      if (!NEEDS_REVIEW.has(t.category) && t.category_source) return false;
    }
    return true;
  });

  const needsReviewCount = transactions.filter(t => t.type === 'expense' && (NEEDS_REVIEW.has(t.category) || !t.category_source)).length;

  const totals = {
    // ── Commandment II: income/expenses from backend summary DTO ──
    income:   transactionSummary.total_income,
    expenses: transactionSummary.total_expenses,
    drafts:   transactions.filter(t => t.status === 'draft').length,
  };

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType   !== 'all') params.append('type', filterType);
      const [res, sumRes] = await Promise.all([
        axios.get(`${API}/transactions?${params}`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/transactions/summary?year=${selectedYear}`, { headers: getAuthHeaders(), withCredentials: true }),
      ]);
      setTransactions(Array.isArray(res.data) ? res.data : (res.data.transactions || []));
      if (sumRes.data) setTransactionSummary(sumRes.data);
    } catch {
      toast.error(t('toast.failed_to_load_transactions'));
    } finally {
      setLoading(false);
    }
  };

  const fetchYears = async () => {
    try {
      const res = await axios.get(`${API}/transactions/years`, { headers: getAuthHeaders(), withCredentials: true });
      const apiYears = Array.isArray(res.data) ? res.data : (res.data.years || []);
      // Always show at least 11 years (current + 10 back) regardless of data — consistent with Dashboard
      const currentYear = new Date().getFullYear();
      const tenYears = Array.from({ length: 11 }, (_, i) => currentYear - i);
      const allYears = [...new Set([...tenYears, ...apiYears])].sort((a, b) => b - a);
      setAvailableYears(allYears);
    } catch {
      const currentYear = new Date().getFullYear();
      setAvailableYears(Array.from({ length: 11 }, (_, i) => currentYear - i));
    }
  };

  useEffect(() => { fetchYears(); }, []);
  useEffect(() => { fetchTransactions(); setSelectedTransactions([]); }, [selectedYear, filterStatus, filterType]);

  // ── Transaction handlers ───────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/transactions`, formData, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(t('toast.transaction_added'));
      setDialogOpen(false);
      setFormData(EMPTY_FORM);
      fetchTransactions();
      fetchYears();
    } catch {
      toast.error(t('toast.failed_to_add_transaction'));
    }
  };

  const handleToggleStatus = async (txn) => {
    const newStatus = txn.status === 'draft' ? 'final' : 'draft';
    try {
      await axios.patch(`${API}/transactions/${txn.transaction_id}/status?status=${newStatus}`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`Marked as ${newStatus}`);
      fetchTransactions();
    } catch {
      toast.error(t('toast.failed_to_update_transaction_status'));
    }
  };

  const handleToggleType = async (id, current) => {
    const newType = current === 'income' ? 'expense' : 'income';
    try {
      await axios.patch(`${API}/transactions/${id}/type?new_type=${newType}`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`Changed to ${newType}`);
      fetchTransactions();
    } catch {
      toast.error('Failed to change transaction type');
    }
  };

  const handleCategoryChange = async (id, category) => {
    try {
      await axios.patch(`${API}/transactions/${id}/category?category=${category}`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(t('toast.category_updated_to', { category: category.replace('_', ' ') }));
      fetchTransactions();
    } catch {
      toast.error(t('toast.failed_to_update_category'));
    }
  };

  const handleDelete = async (id) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      setTimeout(() => setConfirmDeleteId(null), 3000);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await axios.delete(`${API}/transactions/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(t('toast.transaction_deleted'));
      fetchTransactions();
    } catch {
      toast.error(t('toast.failed_to_delete_transaction'));
    }
  };

  // ── Bulk handlers ──────────────────────────────────────────────────────────
  const handleSelectAll = () => {
    if (selectedTransactions.length === filteredTransactions.length && filteredTransactions.length > 0) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(filteredTransactions.map(t => t.transaction_id));
    }
  };

  const handleBulkFinalize = async () => {
    try {
      await axios.post(`${API}/transactions/bulk-status`, { transaction_ids: selectedTransactions, status: 'final' }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`${selectedTransactions.length} transactions marked as final`);
      setSelectedTransactions([]);
      fetchTransactions();
    } catch {
      toast.error(t('toast.failed_to_update_transaction_status'));
    }
  };

  const handleBulkDraft = async () => {
    try {
      await axios.post(`${API}/transactions/bulk-status`, { transaction_ids: selectedTransactions, status: 'draft' }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`${selectedTransactions.length} transactions marked as draft`);
      setSelectedTransactions([]);
      fetchTransactions();
    } catch {
      toast.error(t('toast.failed_to_update_transaction_status'));
    }
  };

  const handleBulkDelete = async () => {
    if (!bulkDeleteArmed) {
      setBulkDeleteArmed(true);
      toast.info(`Click again to delete ${selectedTransactions.length} transactions`, { duration: 3000 });
      setTimeout(() => setBulkDeleteArmed(false), 3000);
      return;
    }
    setBulkDeleteArmed(false);
    try {
      const res = await axios.post(`${API}/transactions/bulk-delete`, selectedTransactions, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(t('toast.deleted_docs', { count: res.data.count }));
      setSelectedTransactions([]);
      fetchTransactions();
      fetchYears();
    } catch {
      toast.error(t('toast.failed_to_delete'));
    }
  };

  const handleBulkSetCategory = async (category) => {
    if (!category || selectedTransactions.length === 0) return;
    try {
      const res = await axios.post(`${API}/transactions/bulk-update-category`, { transaction_ids: selectedTransactions, category }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`${res.data.updated} transaction${res.data.updated !== 1 ? 's' : ''} set to ${(res.data.label || category).split(' (')[0]}`);
      setSelectedTransactions([]);
      fetchTransactions();
    } catch {
      toast.error('Failed to update categories. Please try again.');
    }
  };

  const handleAutoCategorize = async () => {
    if (selectedTransactions.length === 0) return;
    setAutoCategorizing(true);
    try {
      const res = await axios.post(`${API}/transactions/bulk-categorize`, { transaction_ids: selectedTransactions, force: false }, { headers: getAuthHeaders(), withCredentials: true });
      const { total_processed, total_skipped, summary = {}, labels = {} } = res.data;
      if (total_processed === 0) {
        toast.info(total_skipped > 0 ? `All ${total_skipped} selected transactions already have a category.` : 'No transactions could be categorized.', { duration: 5000 });
      } else {
        const breakdown = Object.entries(summary).sort(([, a], [, b]) => b - a).map(([cat, count]) => `${count} ${(labels?.[cat] || cat).split(' (')[0]}`).join(', ');
        toast.success(`${total_processed} transaction${total_processed !== 1 ? 's' : ''} categorized${total_skipped > 0 ? ` · ${total_skipped} skipped` : ''}: ${breakdown}`, { duration: 7000 });
        fetchTransactions();
      }
      setSelectedTransactions([]);
    } catch {
      toast.error('Auto-categorization failed. Please try again.');
    } finally {
      setAutoCategorizing(false);
    }
  };

  // ── Export + AI parse ──────────────────────────────────────────────────────
  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/transactions/export/csv?year=${selectedYear}`, { headers: getAuthHeaders(), withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${selectedYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('toast.failed_to_export_csv'));
    }
  };

  const handleParseContent = async () => {
    if (!parseContent.trim()) return;
    setParsing(true);
    try {
      // Commandment V: use dedicated parse-text endpoint (no union content_type branching)
      const res = await axios.post(`${API}/ai/parse-text`, { content: parseContent, content_type: 'text' }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(t('toast.parsed_transactions', { count: res.data.count }));
      if (res.data.years_found?.length > 0) toast.info(t('toast.years_found', { years: res.data.years_found.join(', ') }));
      setParseDialogOpen(false);
      setParseContent('');
      fetchTransactions();
      fetchYears();
    } catch {
      toast.error(t('toast.failed_to_parse_content'));
    } finally {
      setParsing(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading && transactions.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout>
        <TooltipProvider>
          <div className="space-y-6" data-testid="transactions-page">

            {/* ── Header ── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold mb-1">{t('transactions.title')}</h1>
                <p className="text-muted-foreground text-sm">{t('transactions.subtitle', 'Track all income and expenses with IRS categorization')}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={() => setImportDocsOpen(true)} data-testid="import-docs-btn">
                  <FolderUp className="w-4 h-4 mr-2" />{t('transactions.import_docs', 'Import from Documents')}
                </Button>
                <Button variant="outline" onClick={() => setCsvDialogOpen(true)} data-testid="import-csv-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />{t('transactions.import_csv', 'Import CSV')}
                </Button>
                <Button variant="outline" onClick={() => setPnlDialogOpen(true)} data-testid="import-pnl-btn">
                  <TrendingUp className="w-4 h-4 mr-2" />{t('transactions.import_pnl', 'Import P&L')}
                </Button>
                <Button variant="outline" onClick={() => setDraftDialogOpen(true)} data-testid="review-drafts-btn">
                  <FileText className="w-4 h-4 mr-2" />{t('transactions.review_drafts', 'Review Drafts')}
                </Button>
                <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
                  <FileOutput className="w-4 h-4 mr-2" />{t('transactions.export_csv', 'Export CSV')}
                </Button>
                <Button variant="outline" onClick={() => setParseDialogOpen(true)} data-testid="parse-content-btn">
                  <FileText className="w-4 h-4 mr-2" />{t('transactions.parse_content', 'AI Parse')}
                </Button>

                {/* Add Transaction */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-transaction-btn">
                      <Plus className="w-4 h-4 mr-2" />Add Entry
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Add Transaction</DialogTitle>
                      <DialogDescription>Add a new income or expense transaction.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={formData.type} onValueChange={v => setFormData({ ...formData, type: v, category: v === 'income' ? 'other_income' : 'other_expense' })}>
                            <SelectTrigger data-testid="txn-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="income">Income</SelectItem>
                              <SelectItem value="expense">Expense</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} data-testid="txn-date" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input placeholder="e.g., Office supplies" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required data-testid="txn-description" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Amount ($)</Label>
                          <Input type="number" step="0.01" placeholder="0.00" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} required data-testid="txn-amount" />
                        </div>
                        <div className="space-y-2">
                          <Label>Category</Label>
                          <Select value={formData.category} onValueChange={v => setFormData({ ...formData, category: v })}>
                            <SelectTrigger data-testid="txn-category"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              {(formData.type === 'income' ? INCOME_CATEGORIES : BASE_CATEGORIES).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {formData.type === 'expense' && (
                        <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Checkbox id="deductible" checked={formData.is_deductible} onCheckedChange={v => setFormData({ ...formData, is_deductible: v })} data-testid="txn-deductible" />
                            <Label htmlFor="deductible">Tax Deductible</Label>
                          </div>
                          {formData.is_deductible && (
                            <div className="space-y-1">
                              <Label>Deductible Percentage</Label>
                              <Input type="number" min="0" max="100" value={formData.deductible_percentage} onChange={e => setFormData({ ...formData, deductible_percentage: parseFloat(e.target.value) })} data-testid="txn-deductible-pct" />
                              <p className="text-xs text-muted-foreground">e.g., Business meals are 50%</p>
                            </div>
                          )}
                        </div>
                      )}
                      <Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}>
                        <SelectTrigger data-testid="txn-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="final">Final</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="submit" className="w-full" data-testid="txn-submit">Add Transaction</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <StepGuide stepId="transactions" />
            <SummaryCards totals={totals} />

            {/* ── Filters ── */}
            <Card className="border-border/40 shadow-soft">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium">Tax Year:</Label>
                    <Select value={selectedYear.toString()} onValueChange={v => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="w-32 bg-primary text-primary-foreground" data-testid="year-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>{availableYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-36" data-testid="status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="draft">Drafts Only</SelectItem>
                      <SelectItem value="final">Final Only</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-36" data-testid="type-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant={filterReview ? 'default' : 'outline'} size="sm"
                    onClick={() => setFilterReview(v => !v)}
                    className={filterReview ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
                    data-testid="needs-review-filter"
                  >
                    <AlertTriangle className="w-4 h-4 mr-1.5" />Needs Review
                    {needsReviewCount > 0 && (
                      <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-semibold ${filterReview ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-800'}`} data-testid="needs-review-count">{needsReviewCount}</span>
                    )}
                  </Button>
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search transactions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-transactions" />
                  </div>
                  <BulkActionsBar
                    selectedCount={selectedTransactions.length}
                    totalFiltered={filteredTransactions.length}
                    bulkDeleteArmed={bulkDeleteArmed}
                    autoCategorizing={autoCategorizing}
                    filterType={filterType}
                    categories={BASE_CATEGORIES}
                    incomeCategories={INCOME_CATEGORIES}
                    onSelectAll={handleSelectAll}
                    onBulkFinalize={handleBulkFinalize}
                    onBulkDraft={handleBulkDraft}
                    onBulkDelete={handleBulkDelete}
                    onBulkSetCategory={handleBulkSetCategory}
                    onAutoCategorize={handleAutoCategorize}
                  />
                </div>
              </CardContent>
            </Card>

            {/* ── Transaction list ── */}
            <Card className="border-border/40 shadow-soft">
              <CardHeader className="pb-3 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{t('transactions.transactions', 'Transactions')}</CardTitle>
                  <Badge variant="secondary">{filteredTransactions.length} {t('common.transactions', 'transactions')}</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredTransactions.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <p className="text-lg font-medium">{t('transactions.no_transactions_found', 'No transactions found')}</p>
                    <p className="text-sm mt-1">{t('transactions.add_or_import', 'Add a transaction or import from a CSV file')}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/40">
                    {filteredTransactions.map(txn => (
                      <TransactionRow
                        key={txn.transaction_id}
                        txn={txn}
                        selected={selectedTransactions.includes(txn.transaction_id)}
                        confirmDeleteId={confirmDeleteId}
                        categories={BASE_CATEGORIES}
                        incomeCategories={INCOME_CATEGORIES}
                        onToggleSelect={id => setSelectedTransactions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                        onToggleStatus={handleToggleStatus}
                        onToggleType={handleToggleType}
                        onCategoryChange={handleCategoryChange}
                        onDelete={handleDelete}
                        onOpenIrsInfo={info => { setSelectedIrsLine(info); setIrsDialogOpen(true); }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TooltipProvider>
      </DashboardLayout>

      {/* ── Extracted dialog components ── */}
      <IrsLineDialog open={irsDialogOpen} onClose={() => setIrsDialogOpen(false)} selectedIrsLine={selectedIrsLine} />
      <DraftsReviewDialog open={draftDialogOpen} onClose={() => setDraftDialogOpen(false)} onComplete={fetchTransactions} />
      <ImportDocsDialog open={importDocsOpen} onClose={() => setImportDocsOpen(false)} onImportComplete={() => { fetchTransactions(); fetchYears(); }} />
      <PnlImportDialog open={pnlDialogOpen} onClose={() => setPnlDialogOpen(false)} onImportComplete={() => { fetchTransactions(); fetchYears(); }} />
      <CsvImportDialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} onImportComplete={() => { fetchTransactions(); fetchYears(); }} />

      {/* ── AI Parse dialog (kept inline — small, self-contained) ── */}
      <Dialog open={parseDialogOpen} onOpenChange={setParseDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>{t('transactions.parse_content_title', 'AI Content Parser')}</DialogTitle>
            <DialogDescription>{t('transactions.parse_content_desc', 'Paste bank statements, invoices, or any financial text for AI extraction')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <textarea
              className="w-full h-48 p-3 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Paste your bank statement, invoice, or any financial text here..."
              value={parseContent}
              onChange={e => setParseContent(e.target.value)}
              data-testid="parse-content-input"
            />
            <Button onClick={handleParseContent} disabled={parsing || !parseContent.trim()} className="w-full" data-testid="parse-content-submit">
              {parsing ? 'Parsing...' : 'Extract Transactions with AI'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
